import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, resolveImage, formatPrice, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PdfPicker } from "@/components/PdfPicker";

const EMPTY_CUSTOMER = {
  first_name: "", last_name: "", email: "", phone: "",
  address_line: "", house_number: "", postal_code: "", city: "", province: "", country: "Italia",
};

export default function Checkout() {
  const { items, clear } = useCart();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(() => {
    try { return { ...EMPTY_CUSTOMER, ...(JSON.parse(localStorage.getItem("st_customer") || "{}")) }; }
    catch { return EMPTY_CUSTOMER; }
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfOptions, setPdfOptions] = useState(null);
  const [giftId, setGiftId] = useState(null);
  const [paidIds, setPaidIds] = useState([]);

  useEffect(() => {
    api.get("/pdfs/checkout").then((r) => setPdfOptions(r.data)).catch(() => {});
  }, []);

  const cartItems = () => items.map((i) => ({ product_id: i.id, quantity: i.qty }));

  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/checkout/quote", { items: cartItems(), gift_pdf_id: giftId, paid_pdf_ids: paidIds });
        setQuote(data);
      } catch (e) {
        toast.error("Errore calcolo totale", { description: formatError(e.response?.data?.detail) || e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [items, giftId, paidIds]); // eslint-disable-line

  const set = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const validate = () => {
    const req = ["first_name", "last_name", "email", "address_line", "house_number", "postal_code", "city", "province", "country"];
    for (const k of req) {
      if (!String(customer[k] || "").trim()) return `Compila il campo ${k.replace("_", " ")}`;
    }
    if (!/^\S+@\S+\.\S+$/.test(customer.email)) return "Email non valida";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      localStorage.setItem("st_customer", JSON.stringify(customer));
      const payload = {
        items: cartItems(),
        gift_pdf_id: giftId,
        paid_pdf_ids: paidIds,
        customer,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      localStorage.setItem("st_last_session", data.session_id);
      // Save cart snapshot so it can be cleared on success
      localStorage.setItem("st_pending_session", data.session_id);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Errore avvio pagamento", { description: formatError(e.response?.data?.detail) || e.message });
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <Layout>
        <div className="px-4 py-12 text-center">
          <p className="text-slate-500">Il carrello è vuoto</p>
          <Link to="/catalogo" data-testid="empty-back" className="mt-4 inline-block bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold rounded-full px-5 py-3 text-sm">
            Scopri il catalogo →
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 pt-6 pb-8">
        <h1 className="font-heading font-black text-2xl text-brand-blue">Checkout</h1>
        <p className="text-sm text-slate-500 mt-1">Puoi acquistare anche senza account</p>

        <form onSubmit={submit} className="mt-5 space-y-5">
          <section className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Dati cliente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nome *</Label>
                <Input data-testid="ck-first-name" value={customer.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div>
                <Label>Cognome *</Label>
                <Input data-testid="ck-last-name" value={customer.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="ck-email" type="email" value={customer.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input data-testid="ck-phone" value={customer.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1 rounded-xl" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Indirizzo di spedizione</h2>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-4">
                <Label>Indirizzo *</Label>
                <Input data-testid="ck-address" value={customer.address_line} onChange={(e) => set("address_line", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div className="sm:col-span-2">
                <Label>Numero civico *</Label>
                <Input data-testid="ck-house" value={customer.house_number} onChange={(e) => set("house_number", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div className="sm:col-span-2">
                <Label>CAP *</Label>
                <Input data-testid="ck-postal" value={customer.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div className="sm:col-span-3">
                <Label>Città *</Label>
                <Input data-testid="ck-city" value={customer.city} onChange={(e) => set("city", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
              <div className="sm:col-span-1">
                <Label>Provincia *</Label>
                <Input data-testid="ck-province" value={customer.province} onChange={(e) => set("province", e.target.value.toUpperCase())} className="mt-1 rounded-xl" maxLength={3} required />
              </div>
              <div className="sm:col-span-3">
                <Label>Paese *</Label>
                <Input data-testid="ck-country" value={customer.country} onChange={(e) => set("country", e.target.value)} className="mt-1 rounded-xl" required />
              </div>
            </div>
          </section>

          <PdfPicker options={pdfOptions} giftId={giftId} setGiftId={setGiftId} paidIds={paidIds} setPaidIds={setPaidIds} />

          <section className="bg-white border border-border rounded-2xl p-4">
            <h2 className="font-heading font-bold text-brand-blue mb-3">Riepilogo ordine</h2>
            {loading ? (
              <p className="text-sm text-slate-500">Calcolo totale...</p>
            ) : quote ? (
              <div className="space-y-3">
                {quote.line_items.map((li) => (
                  <div key={li.product_id} data-testid={`sum-item-${li.product_id}`} className="flex gap-3 items-center">
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                      {li.image && <img src={resolveImage(li.image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{li.title}</div>
                      <div className="text-xs text-slate-500">Qta {li.quantity} · {formatPrice(li.unit_price)} cad.</div>
                    </div>
                    <div className="text-sm font-heading font-black text-brand-blue">{formatPrice(li.subtotal)}</div>
                  </div>
                ))}
                {quote.digital_items?.map((d) => (
                  <div key={d.pdf_id} data-testid={`sum-pdf-${d.pdf_id}`} className="flex justify-between text-sm">
                    <span className="truncate">PDF: {d.title}</span>
                    <span className={d.kind === "gift" ? "text-emerald-600 font-bold" : "font-bold text-brand-blue"}>
                      {d.kind === "gift" ? "OMAGGIO" : formatPrice(d.unit_price)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-border pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Subtotale prodotti</span><span data-testid="sum-subtotal">{formatPrice(quote.subtotal)}</span></div>
                  {quote.pdf_subtotal > 0 && (
                    <div className="flex justify-between"><span>PDF digitali</span><span data-testid="sum-pdf-subtotal">{formatPrice(quote.pdf_subtotal)}</span></div>
                  )}
                  <div className="flex justify-between">
                    <span>Spedizione</span>
                    <span data-testid="sum-shipping" className={quote.shipping === 0 ? "text-emerald-600 font-bold" : ""}>
                      {quote.shipping === 0 ? "GRATUITA" : formatPrice(quote.shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-2 border-t border-border mt-2">
                    <span className="font-heading font-bold text-brand-blue">Totale</span>
                    <span data-testid="sum-total" className="font-heading font-black text-2xl text-brand-blue">
                      {formatPrice(quote.total)}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          <Button
            type="submit"
            data-testid="pay-stripe-btn"
            disabled={submitting || loading || !quote}
            className="w-full h-14 rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-black text-lg"
          >
            {submitting ? "Reindirizzamento..." : "Paga in sicurezza con Stripe"}
          </Button>
          <p className="text-xs text-center text-slate-400">
            Pagamento gestito da Stripe – i dati della tua carta non vengono mai memorizzati sui nostri server.
          </p>
        </form>
      </div>
    </Layout>
  );
}
